import { useState, useImperativeHandle, forwardRef } from "react";
import { motion } from "framer-motion";
import { Swords, Star, Globe, MapPin, User, Users, Clock, Shield, CheckCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";

// Session-level cache for validated characters (key: lowercase name)
const charValidationCache = new Map<string, { data: any; date: string }>();

const capitalizeCharName = (name: string) =>
  name.replace(/\b\w/g, (c) => c.toUpperCase());

interface CharacterInfo {
  name: string;
  level: number;
  vocation: string;
  world: string;
  residence: string;
  sex: string;
  guild?: { name: string; rank: string };
  account_status: string;
  last_login?: string;
}

export interface ValidateResult {
  found?: boolean;
  cached?: boolean;
  rateLimited?: boolean;
  retryAfter?: number;
}

export interface RubinotCharacterValidatorHandle {
  /**
   * Validates a character name. Opens the appropriate dialogs.
   * Resolves with a result describing whether it was cached or rate-limited,
   * so callers can drive a cooldown UI.
   */
  validate: (name: string) => Promise<ValidateResult>;
}

interface Props {
  /** When the user confirms a found character, called with the canonical name + world + extra info */
  onConfirm: (info: {
    name: string;
    world: string;
    level?: number;
    vocation?: string;
    residence?: string;
    sex?: string;
    guild?: string;
  }) => void;
  /** When validation fails and user opts to proceed anyway (only if requireValidation=false) */
  onProceedUnvalidated?: (name: string) => void;
  /** When user clicks "Corrigir Nome" */
  onCorrect?: () => void;
  /** If true, the not-found dialog will not offer "Sim, Prosseguir" */
  requireValidation?: boolean;
}

export const RubinotCharacterValidator = forwardRef<
  RubinotCharacterValidatorHandle,
  Props
>(({ onConfirm, onProceedUnvalidated, onCorrect, requireValidation = true }, ref) => {
  const [showValidating, setShowValidating] = useState(false);
  const [showFound, setShowFound] = useState(false);
  const [showNotFound, setShowNotFound] = useState(false);
  const [character, setCharacter] = useState<CharacterInfo | null>(null);
  const [pendingName, setPendingName] = useState("");

  useImperativeHandle(ref, () => ({
    validate: async (name: string): Promise<ValidateResult> => {
      const trimmed = name.trim();
      if (!trimmed) return { found: false };
      setPendingName(trimmed);
      setShowValidating(true);

      const normalized = capitalizeCharName(trimmed);
      const cacheKey = normalized.toLowerCase();
      const today = new Date().toISOString().slice(0, 10);
      const cached = charValidationCache.get(cacheKey);

      try {
        let data: any;
        let fromCache = false;
        if (cached && cached.date === today) {
          data = cached.data;
          fromCache = true;
        } else {
          const res = await supabase.functions.invoke("fetch-rubinot-character", {
            body: { name: normalized },
          });
          if (res.error) {
            // Detect rate-limit (HTTP 429) so the caller can start a cooldown.
            const ctx: any = (res.error as any).context;
            if (ctx && typeof ctx.json === "function") {
              try {
                const body = await ctx.json();
                if (ctx.status === 429 || body?.error === "rate_limited") {
                  setShowValidating(false);
                  const retryAfter = Number(body?.retryAfter) || 60;
                  return { rateLimited: true, retryAfter };
                }
              } catch {
                /* ignore parse error */
              }
            }
            throw res.error;
          }
          data = res.data;
          charValidationCache.set(cacheKey, { data, date: today });
        }

        setShowValidating(false);

        if (data?.found && data.character) {
          const c = data.character;
          setCharacter({
            name: c.name,
            level: c.level || 0,
            vocation: c.vocation || "Unknown",
            world: c.world || "Unknown",
            residence: c.residence || "Unknown",
            sex: c.sex || "Unknown",
            guild: c.guild ? { name: c.guild, rank: "" } : undefined,
            account_status: c.account_status || "Unknown",
            last_login: c.last_login || undefined,
          });
          setShowFound(true);
          return { found: true, cached: fromCache || !!data?.cached };
        } else {
          setShowNotFound(true);
          return { found: false, cached: fromCache || !!data?.cached };
        }
      } catch {
        setShowValidating(false);
        setShowNotFound(true);
        return { found: false };
      }
    },
  }));

  const handleConfirmFound = () => {
    if (character) {
      onConfirm({
        name: character.name,
        world: character.world,
        level: character.level,
        vocation: character.vocation,
        residence: character.residence,
        sex: character.sex,
        guild: character.guild?.name,
      });
    }
    setShowFound(false);
  };

  return (
    <>
      {/* Validating */}
      <Dialog open={showValidating} onOpenChange={() => {}}>
        <DialogContent
          className="tibia-border bg-card max-w-xs [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-pixel text-xs text-primary gold-text-glow text-center">
              ⚔️ Validando Personagem
            </DialogTitle>
            <DialogDescription className="sr-only">
              Validando personagem no servidor
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <Swords className="w-10 h-10 text-primary" />
            </motion.div>
            <motion.p
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="font-pixel text-[9px] text-muted-foreground text-center"
            >
              Validando personagem no servidor RubinOT...
            </motion.p>
            <div className="w-full tibia-border-inner bg-secondary/30 rounded-sm p-2">
              <motion.div
                className="h-1 bg-primary rounded-full"
                animate={{ width: ["0%", "100%"] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Found */}
      <Dialog open={showFound} onOpenChange={setShowFound}>
        <DialogContent
          preventOutsideClose
          className="tibia-border bg-card max-w-md p-0 overflow-hidden max-h-[85vh] overflow-y-auto [&>button]:hidden"
        >
          <div className="tibia-border-inner bg-secondary px-4 py-3 -mx-[1px] -mt-[1px]">
            <DialogHeader>
              <DialogTitle className="font-pixel text-xs text-primary gold-text-glow text-center uppercase tracking-wider">
                ⚔️ Personagem Encontrado
              </DialogTitle>
              <DialogDescription className="sr-only">
                Informações do personagem
              </DialogDescription>
            </DialogHeader>
          </div>
          {character && (
            <div className="p-4 space-y-4">
              <div className="text-center space-y-1">
                <p className="font-pixel text-base text-primary gold-text-glow">
                  Perfil de {character.name}
                </p>
                <p className="font-pixel text-[9px] text-muted-foreground">
                  Level {character.level} {character.vocation}
                </p>
                <p className="font-pixel text-[8px] text-muted-foreground">
                  Mundo: {character.world}
                </p>
              </div>

              <div className="tibia-border-inner bg-secondary/50 rounded-sm p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Star className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="font-pixel text-[8px] text-muted-foreground uppercase w-16">
                    Nível
                  </span>
                  <span className="font-pixel text-[9px] text-foreground">
                    {character.level}
                  </span>
                </div>
                <div className="h-px bg-border/50" />
                <div className="flex items-center gap-2">
                  <Swords className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="font-pixel text-[8px] text-muted-foreground uppercase w-16">
                    Vocação
                  </span>
                  <span className="font-pixel text-[9px] text-foreground">
                    {character.vocation}
                  </span>
                </div>
                <div className="h-px bg-border/50" />
                <div className="flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="font-pixel text-[8px] text-muted-foreground uppercase w-16">
                    Mundo
                  </span>
                  <span className="font-pixel text-[9px] text-foreground">
                    {character.world}
                  </span>
                </div>
                <div className="h-px bg-border/50" />
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="font-pixel text-[8px] text-muted-foreground uppercase w-16">
                    Cidade
                  </span>
                  <span className="font-pixel text-[9px] text-foreground">
                    {character.residence}
                  </span>
                </div>
                <div className="h-px bg-border/50" />
                <div className="flex items-center gap-2">
                  <User className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="font-pixel text-[8px] text-muted-foreground uppercase w-16">
                    Sexo
                  </span>
                  <span className="font-pixel text-[9px] text-foreground">
                    {character.sex}
                  </span>
                </div>
                {character.guild && (
                  <>
                    <div className="h-px bg-border/50" />
                    <div className="flex items-center gap-2">
                      <Users className="w-3.5 h-3.5 text-primary shrink-0" />
                      <span className="font-pixel text-[8px] text-muted-foreground uppercase w-16">
                        Guilda
                      </span>
                      <span className="font-pixel text-[9px] text-foreground truncate">
                        {(() => {
                          const raw = character.guild!.rank
                            ? `${character.guild!.rank} da ${character.guild!.name}`
                            : character.guild!.name;
                          return raw
                            .replace(/\s*\[([^\]]+)\]\([^)]*\)/g, " $1")
                            .replace(/\s*\([^)]*\)\s*/g, "")
                            .trim();
                        })()}
                      </span>
                    </div>
                  </>
                )}
                {character.last_login && (
                  <>
                    <div className="h-px bg-border/50" />
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-primary shrink-0" />
                      <span className="font-pixel text-[8px] text-muted-foreground uppercase w-16">
                        Último Login
                      </span>
                      <span className="font-pixel text-[9px] text-foreground">
                        {(() => {
                          const d = new Date(character.last_login!);
                          return isNaN(d.getTime())
                            ? character.last_login
                            : d.toLocaleString("pt-BR");
                        })()}
                      </span>
                    </div>
                  </>
                )}
                <div className="h-px bg-border/50" />
                <div className="flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="font-pixel text-[8px] text-muted-foreground uppercase w-16">
                    Conta
                  </span>
                  <span className="font-pixel text-[9px] text-foreground">
                    {character.account_status}
                  </span>
                </div>
              </div>

              <div className="text-center">
                <a
                  href={`https://rubinot.com.br/characters?name=${encodeURIComponent(character.name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-primary hover:underline"
                >
                  Ver no Rubinot ↗
                </a>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleConfirmFound}
                className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                <span className="font-pixel text-[9px]">Confirmar Personagem</span>
              </motion.button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Not Found */}
      <AlertDialog open={showNotFound} onOpenChange={setShowNotFound}>
        <AlertDialogContent className="tibia-border bg-card max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-pixel text-xs text-destructive text-center">
              ⚠️ Personagem Não Encontrado
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-muted-foreground text-center">
              {requireValidation ? (
                <>
                  Não foi possível validar o personagem "
                  <span className="text-primary">{pendingName}</span>". É necessário
                  informar um personagem válido para prosseguir.
                </>
              ) : (
                <>
                  Não foi possível validar o personagem "
                  <span className="text-primary">{pendingName}</span>". Deseja prosseguir
                  mesmo assim?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-2 sm:justify-center">
            <AlertDialogCancel
              onClick={() => {
                setShowNotFound(false);
                onCorrect?.();
              }}
              className="font-pixel text-[8px] mt-0"
            >
              Corrigir Nome
            </AlertDialogCancel>
            {!requireValidation && (
              <AlertDialogAction
                onClick={() => {
                  setShowNotFound(false);
                  onProceedUnvalidated?.(pendingName);
                }}
                className="font-pixel text-[8px]"
              >
                Sim, Prosseguir
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

RubinotCharacterValidator.displayName = "RubinotCharacterValidator";
